/**
 * Updates navigator.onLine property based on network connectivity status.
 * This property is checked by API utilities before making network requests.
 *
 * @param isOnline - Network connectivity status (true = connected, false = disconnected)
 */
const handleNetworkStatusChange = (isOnline: boolean): void => {
    Object.defineProperty(navigator, 'onLine', {
        writable: true,
        configurable: true,
        value: isOnline,
    })
}

/**
 * Initializes network monitoring for the platform.
 *
 * Listens to network status messages from the React Native layer
 * and updates navigator.onLine accordingly. The updated navigator.onLine value
 * is used by API utilities (like virgoPostCall) to prevent network calls when offline.
 *
 * Flow:
 * 1. React Native detects network changes
 * 2. React Native sends postMessage with networkStatusChanged type
 * 3. This service updates navigator.onLine
 * 4. UI callback triggers banner display
 * 5. API calls check navigator.onLine before executing
 *
 * @param onStatusChange - Callback invoked when network status changes (used for UI banner)
 * @returns Cleanup function to remove event listener
 */
export const initNetworkMonitoring = (
    onStatusChange: (isOnline: boolean) => void,
): (() => void) => {
    const handleMessage = (event: MessageEvent): void => {
        // Verify message origin - only accept messages from React Native WebView (same origin)
        // React Native WebView messages come from the same origin as the app
        if (event.origin !== window.location.origin) {
            return
        }

        const isOnline = event.data?.parameters?.isOnline

        // Only process networkStatusChanged messages with valid boolean isOnline
        if (event.data?.type === 'networkStatusChanged' && typeof isOnline === 'boolean') {
            console.log('[Platform] Network event received - isOnline:', isOnline)

            // Update navigator.onLine for API utilities
            handleNetworkStatusChange(isOnline)

            // Notify UI component to show/hide banner
            onStatusChange(isOnline)
        }
    }

    // Register listener for messages from React Native
    window.addEventListener('message', handleMessage)

    // Return cleanup function for component unmount
    return (): void => {
        window.removeEventListener('message', handleMessage)
    }
}
